import React, { FC, useState, useEffect } from 'react'
import { IconButton, makeStyles, Theme } from '@material-ui/core'
import classnames from 'classnames'

import { RouteTransition } from 'components'
import { EventStages } from 'types'
import { useAppState } from 'providers'
import { ReactComponent as LiveIcon } from 'assets/live-icon.svg'
import { createInteraction } from 'helpers'

interface VimeoLiveStreamProps {
  useBackupStream: Boolean
  eventStage?: EventStages
  setPostLiveStream: (changeVal: boolean) => void
}

export const VimeoLiveStream: FC<VimeoLiveStreamProps> = ({ useBackupStream, eventStage, setPostLiveStream }) => {
  const classes = useStyles()
  const {
    appState: { user, showLivestream }
  } = useAppState()
  const [redirectTrigger, setRedirectTrigger] = useState<boolean>(false)

  const generateInteraction = (trigger: string) => {
    if (user?.id) {
      createInteraction('livestream', user?.email || '', 'livestream', trigger, eventStage)
    }
  }

  const exit = () => {
    if (eventStage && [EventStages.LIVESTREAMENDING, EventStages.POSTLIVESTREAM].includes(eventStage)) {
      setPostLiveStream(true)
    }
    setRedirectTrigger(true)
  }

  useEffect(() => {
    generateInteraction('joined')
    return () => {
      generateInteraction('left')
    }
    // eslint-disable-next-line
  }, [])

  return (
    <>
      <div className={classes.root}>
        <div className={classes.streamSideFull}>
          <IconButton
            className={classnames(classes.liveIndicator, !showLivestream && classes.livestreamDisabled)}
            onClick={() => null}
            disableFocusRipple
            disableRipple
            disableTouchRipple
          >
            <LiveIcon width={40} height={30} className={classnames(showLivestream && 'pulse')} />
          </IconButton>
          <IconButton
            className={classes.closeButton}
            onClick={exit}
            disableFocusRipple
            disableRipple
            disableTouchRipple
          >
            Exit
          </IconButton>
          {!useBackupStream ? (
            <>
              <iframe
                className={classnames([classes.iframe, classes.iframeFull])}
                title='Verizon 5G'
                src='https://vimeo.com/event/501316/embed'
                allow='autoplay; fullscreen'
                allowFullScreen
              ></iframe>
            </>
          ) : (
            <>
              <iframe
                className={classnames([classes.iframe, classes.iframeFull])}
                title='Verizon 5G'
                src='https://vimeo.com/event/501328/embed'
                allow='autoplay; fullscreen'
                allowFullScreen
              ></iframe>
            </>
          )}
        </div>
      </div>
      <RouteTransition animationTrigger={redirectTrigger} route='/event' timeout={300} referrer='stream' />
    </>
  )
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    height: '100%',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    background: 'black'
  },
  iframe: {
    zIndex: 1,
    width: '100%',
    border: 'none',
    transition: 'all 200ms',
    minHeight: '100vh',
    maxHeight: '100vh',
    [`${theme.breakpoints.down('sm')}`]: {
      minHeight: '40vh'
    },
    [`${theme.breakpoints.down('sm')} and (orientation: landscape)`]: {
      minHeight: '100vh'
    }
  },
  iframeFull: {
    width: '100%'
  },
  iframeWithChat: {
    width: 'calc(100% - 351px)'
  },
  closeButton: {
    color: 'white',
    position: 'absolute',
    top: 30,
    right: 30,
    zIndex: 10,
    '& svg': {
      color: '#fff'
    }
  },
  liveIndicator: {
    position: 'absolute',
    top: 30,
    left: 30,
    zIndex: 10,
    '& svg': {
      color: '#fff'
    }
  },
  toastESSButton: {
    height: '24px',
    fontSize: '12px',
    marginBottom: '1rem',
    padding: '0 20px',
    display: 'flex',
    flexWrap: 'nowrap',
    justifyContent: 'center',
    alignItems: 'center',
    '&:hover': {
      backgroundColor: 'black',
      color: 'white'
    }
  },
  streamSideWithChat: {
    width: 'calc(100% - 351px)'
  },
  streamSideFull: {
    width: '100%'
  },
  chatSide: {},
  hamburgerMenu: {
    position: 'absolute',
    top: '30px',
    right: '45px',
    display: 'inline-block',
    cursor: 'pointer'
  },
  messagePaper: {
    '&.MuiPaper-root': {
      backgroundColor: 'white !important'
    }
  },
  logo: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    background: 'white',
    minHeight: '60px',
    maxHeight: '60px'
  },
  mainMenu: {
    height: 'calc(100% - 60px)',
    overflow: 'scroll'
  },
  displayMenu: {
    width: '350px',
    color: 'white',
    height: 'calc(100% - 60px)',
    display: 'flex',
    flexDirection: 'column'
  },
  toolbar: {
    paddingRight: 0,
    paddingLeft: 0,
    alignItems: 'flex-start',
    minHeight: '50px',
    borderBottom: '1px solid #D8DADA'
  },
  tabs: {
    display: 'flex',
    width: '100%',
    color: 'black'
  },
  tab: {
    flex: 1,
    minWidth: '33.33%',
    maxWidth: '100%'
  },
  tabPanel: { flex: 1 },
  peoplePanel: {
    '& .MuiBox-root': {
      paddingTop: 0
    }
  },
  black: {
    background: 'black'
  },
  dialog: {
    position: 'absolute',
    top: 0,
    width: '100%'
  },
  avatarContainer: {
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    color: 'white',
    alignItems: 'center',
    position: 'relative'
  },
  avatarCircle: {
    width: '100px',
    height: '100px',
    background: 'blue',
    borderRadius: '50%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 9
  },
  avatarLetter: {
    fontSize: '3rem'
  },
  livestreamDisabled: {
    color: '#979797',
    '& svg': {
      '& .icon-bg': {
        fill: '#979797 !important'
      }
    }
  }
}))
